import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TicketTable = ({ tickets, onTicketClick, sortConfig, onSort, selectedIds, onToggleSelect, onToggleSelectAll, onConvertToInvoice, onEditTicket }) => {
  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical':
        return 'bg-error/10 text-error border-error/20';
      case 'high':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'medium':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'low':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'in-progress':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'on-hold':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'cancelled':
        return 'bg-error/10 text-error border-error/20';
      case 'pending':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.column !== column) return 'ChevronsUpDown';
    return sortConfig?.direction === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const allSelected = tickets?.length > 0 && tickets?.every(t => selectedIds?.includes(t?.id));
  const someSelected = tickets?.some(t => selectedIds?.includes(t?.id));

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 md:py-4 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={el => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={() => onToggleSelectAll(tickets)}
                  onClick={e => e?.stopPropagation()}
                  className="w-4 h-4 rounded border-border text-primary cursor-pointer"
                />
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left">
                <button
                  onClick={() => onSort('ticketId')}
                  className="flex items-center gap-2 text-xs md:text-sm font-caption font-medium text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Ticket ID
                  <Icon name={getSortIcon('ticketId')} size={16} color="currentColor" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left">
                <button
                  onClick={() => onSort('customer')}
                  className="flex items-center gap-2 text-xs md:text-sm font-caption font-medium text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Customer
                  <Icon name={getSortIcon('customer')} size={16} color="currentColor" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left hidden lg:table-cell">
                <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                  Technicians
                </span>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left hidden xl:table-cell">
                <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                  Salesperson
                </span>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left hidden md:table-cell">
                <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                  Service Type
                </span>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left">
                <button
                  onClick={() => onSort('priority')}
                  className="flex items-center gap-2 text-xs md:text-sm font-caption font-medium text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Priority
                  <Icon name={getSortIcon('priority')} size={16} color="currentColor" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left">
                <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                  Status
                </span>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-left hidden lg:table-cell">
                <button
                  onClick={() => onSort('scheduledDate')}
                  className="flex items-center gap-2 text-xs md:text-sm font-caption font-medium text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Scheduled
                  <Icon name={getSortIcon('scheduledDate')} size={16} color="currentColor" />
                </button>
              </th>
              <th className="px-4 md:px-6 py-3 md:py-4 text-right">
                <span className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tickets?.map((ticket) => (
              <tr
                key={ticket?.id}
                onClick={() => onTicketClick(ticket)}
                className={`hover:bg-muted/30 cursor-pointer transition-smooth ${
                  selectedIds?.includes(ticket?.id) ? 'bg-primary/5' : ''
                }`}
              >
                <td className="px-4 py-3 md:py-4" onClick={e => e?.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(ticket?.id)}
                    onChange={() => onToggleSelect(ticket?.id)}
                    className="w-4 h-4 rounded border-border text-primary cursor-pointer"
                  />
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span className="text-sm md:text-base font-medium text-foreground">
                    {ticket?.ticketId}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <div className="flex flex-col">
                    <span className="text-sm md:text-base font-medium text-foreground">
                      {ticket?.customer}
                    </span>
                    <span className="text-xs text-muted-foreground line-clamp-1">
                      {ticket?.customerEmail}
                    </span>
                  </div>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                  <div className="flex flex-wrap gap-1">
                    {ticket?.technicians?.slice(0, 2)?.map((tech, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs font-medium"
                      >
                        {tech}
                      </span>
                    ))}
                    {ticket?.technicians?.length > 2 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md bg-muted text-muted-foreground text-xs font-medium">
                        +{ticket?.technicians?.length - 2}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden xl:table-cell">
                  {ticket?.salesperson ? (
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-success/10 text-success text-xs font-medium">
                      {ticket?.salesperson}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not assigned</span>
                  )}
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden md:table-cell">
                  <span className="text-sm text-foreground">{ticket?.serviceType}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span
                    className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium border ${getPriorityColor(
                      ticket?.priority
                    )}`}
                  >
                    {ticket?.priority}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4">
                  <span
                    className={`inline-flex items-center px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium border ${getStatusColor(
                      ticket?.status
                    )}`}
                  >
                    {ticket?.status}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 hidden lg:table-cell">
                  <span className="text-sm text-foreground">{formatDate(ticket?.scheduledDate)}</span>
                </td>
                <td className="px-4 md:px-6 py-3 md:py-4 text-right">
                  {onEditTicket && ticket?.status?.toLowerCase() !== 'completed' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        onEditTicket(ticket);
                      }}
                      iconName="Edit"
                      title="Edit Ticket"
                    />
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e?.stopPropagation();
                      onTicketClick(ticket);
                    }}
                    iconName="ChevronRight"
                  >
                    <span className="hidden md:inline">View</span>
                  </Button>
                  {ticket?.status?.toLowerCase() === 'completed' && !ticket?.convertedToInvoice && onConvertToInvoice && (
                    <button
                      onClick={(e) => { e?.stopPropagation(); onConvertToInvoice(ticket); }}
                      className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-success/10 text-success hover:bg-success/20 transition-colors"
                      title="Convert to Invoice"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                      Invoice
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TicketTable;